import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { AppState } from 'src/app/core/app.state';
import { AuthReturnUrlService } from 'src/app/core/services/auth-return-url.service';
import { clearPermissions } from 'src/app/core/store/permissions/permissions.actions';
import { AuthService } from 'src/app/features/auth/authentication/services/auth.service';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { clearStorageExceptPreserved } from 'src/app/shared/preserved-storage-keys';
import { isReturnableUrl } from './returnable-url';

/**
 * Single owner of "the session is over".
 *
 * Sessions are server-side now: clearing localStorage alone leaves the refresh
 * token live, so a copy could keep minting access tokens after Log out. One
 * place to do it keeps the two logout buttons and the interceptor in step.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  constructor(
    private readonly router: Router,
    private readonly store: Store<AppState>,
    private readonly authService: AuthService,
    private readonly returnUrl: AuthReturnUrlService
  ) {}

  /**
   * User-initiated sign-out: revoke server-side, then drop local state.
   *
   * Fire-and-forget - the token travels in the body, so the call survives the
   * local clear, and a network failure must not strand the user signed in.
   */
  logout(): void {
    const refreshToken = LocalStorageService.get('currentUser')?.refresh_token;

    // Deliberate sign-out, so drop any remembered deep link: the next person to
    // log in on this tab should start from the orders list, not on the screen
    // the previous session ended on.
    this.returnUrl.clear();

    if (refreshToken) {
      this.authService
        .logout(refreshToken)
        .subscribe({ error: () => undefined });
    }

    this.clearLocalSession();
  }

  /**
   * The session ended underneath the user rather than being ended by them, so
   * remember the page they were on and hand it back once they sign in again.
   */
  expireSession(): Promise<boolean> {
    // Only from a page worth returning to. A page usually fires several requests
    // at once, so the later 401s land after the redirect, when the URL is already
    // the login page - capturing that would wipe the link the first one
    // remembered.
    if (isReturnableUrl(this.router.url)) {
      this.returnUrl.capture(this.router.url);
    }

    return this.clearLocalSession();
  }

  /**
   * Drop local session state and return to the login page.
   *
   * For paths where the server side is already settled (a rejected refresh);
   * calling logout there would revoke an already-dead token.
   */
  clearLocalSession(): Promise<boolean> {
    this.store.dispatch(clearPermissions());
    clearStorageExceptPreserved();
    return this.router.navigate(['']);
  }
}
